import { ApolloClient, ApolloLink, HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RouterContextProvider } from 'react-router';
import { AWS_GRAPHQL2_API_URL } from '@/env';
import { tokenProviderContext } from '@/request-context.ts';
import { traceError } from '@/utils/trace-error';
import { makeApolloCache } from './apollo-cache';
import { shouldReportGraphQLError } from './graphql-error-reporting';

export const makeSsrApolloClient = (
  context?: Readonly<RouterContextProvider>,
) => {
  const authLink = setContext(async (_, { headers }) => {
    const tokens = await context?.get(tokenProviderContext).getTokens();

    const originVerifyHeader = process.env.GQL_ORIGIN_VERIFY_SECRET
      ? { 'x-origin-verify': process.env.GQL_ORIGIN_VERIFY_SECRET }
      : {};

    if (tokens) {
      return {
        headers: {
          ...headers,
          Authorization: `Cognito ${tokens.accessToken}`,
          ...originVerifyHeader,
        },
      };
    }

    return {
      headers: { ...headers, ...originVerifyHeader },
    };
  });

  const errorLink = onError(({ networkError, graphQLErrors }) => {
    if (graphQLErrors)
      graphQLErrors.forEach((error) => {
        if (!shouldReportGraphQLError(error)) return;

        traceError(new Error('[GraphQL error]: ' + error.message));
      });
    if (networkError) {
      traceError(
        new Error('[GraphQL network error]: ' + networkError.message, {
          cause: networkError,
        }),
      );
    }
  });

  return new ApolloClient({
    ssrMode: true,
    cache: makeApolloCache(),
    link: ApolloLink.from([
      authLink,
      errorLink,
      new HttpLink({
        uri: (operation) =>
          `${AWS_GRAPHQL2_API_URL}?${operation.operationName}`,
      }),
    ]),
  });
};
